import {
  Button,
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@ibsys/kit';
import { PlusIcon } from 'lucide-react';
import { FC, useMemo, useState } from 'react';
import { IconButton } from '../IconButton';

/** Вариант, который можно добавить в набор */
export type AddOption = {
  value: string;
  label: string;
};

type Props = {
  /** Название действия: подпись кнопки, а у кнопки-значка — её доступное имя и подсказка */
  label: string;
  /** Кнопка обозначена только значком «+» */
  isIconOnly?: boolean;
  /** Варианты, которые ещё можно добавить: уже добавленные потребитель в них не передаёт */
  options: AddOption[];
  /** Подсказка в поле поиска по вариантам */
  searchPlaceholder: string;
  /** Сообщение, когда ни один вариант не подошёл под поиск */
  emptyMessage: string;
  onAdd: (value: string) => void;
  disabled?: boolean;
};

/**
 * Кнопка «+» с окном выбора варианта из перечня с поиском — оболочка добавления элемента в набор
 * (атрибута в панель фильтров, свойства в набор значений). Состав вариантов остаётся у потребителя,
 * вид окна и поиск по подписи варианта заданы здесь.
 */
export const AddOptionPopover: FC<Props> = ({
  label,
  isIconOnly = false,
  options,
  searchPlaceholder,
  emptyMessage,
  onAdd,
  disabled,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');

  const filteredOptions = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return options;
    return options.filter((option) => option.label.toLowerCase().includes(query));
  }, [options, search]);

  const handleSelect = (value: string) => {
    onAdd(value);
    setIsOpen(false);
    setSearch('');
  };

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    // Повторное открытие показывает перечень целиком, а не отобранный прошлым поиском
    if (!open) setSearch('');
  };

  return (
    <Popover open={isOpen} onOpenChange={handleOpenChange}>
      {isIconOnly ? (
        <IconButton
          variant="secondary"
          size="icon"
          icon={<PlusIcon />}
          label={label}
          trigger={PopoverTrigger}
          disabled={disabled}
        />
      ) : (
        <PopoverTrigger asChild>
          <Button variant="secondary" size="sm" icon={<PlusIcon />} disabled={disabled}>
            {label}
          </Button>
        </PopoverTrigger>
      )}
      <PopoverContent className="w-80 p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput placeholder={searchPlaceholder} value={search} onValueChange={setSearch} />
          <CommandList>
            <CommandEmpty>{emptyMessage}</CommandEmpty>
            {filteredOptions.map((option) => (
              <CommandItem key={option.value} value={option.value} onSelect={handleSelect}>
                {option.label}
              </CommandItem>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
